import React from 'react';
import { ApiOrganisationDataType, ApiScreeningMatchProvider } from '../api';
import { useDispatch } from 'react-redux';
import {
  CAN_DELETE_ORGANISATION_DATA,
  CAN_MODIFY_SEARCH_INDEX,
  CAN_TRIGGER_PERSON_SCREENABLE_VALUE_CALCULATION,
} from '../auth/permissions';
import Has from '../auth/Has';
import { SalvButton, SalvCard, Size } from '@salvapp/ui-components';
import { InternalApiCurrentUserOrganisation, InternalApiIndexType } from '../api-internal';
import useCustomLists from '../custom-lists/useCustomLists';
import { calculatePersonScreenableValues, recreateIndex } from './searchIndexActions';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import SalvPage from '../common/SalvPage';
import { Dispatch } from 'redux';
import { deleteOrganisationData } from '../organisation/organisationActions';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useMe } from '../organisation/useMe';

const recreateSearchIndex = async (
  indexType: InternalApiIndexType | undefined,
  customListCode: string | undefined,
  dispatch: Dispatch<any>
) => {
  dispatch(
    showConfirmationModal({
      title: indexType
        ? `Are you sure you wish to recreate ${indexType} index?`
        : `Are you sure you wish to recreate ${customListCode} index?`,
      confirmationText:
        indexType === InternalApiIndexType.DOW_JONES ? 'Yes, create all DJ indexes' : 'Yes, create new index',
      cancelText: 'No, keep it',
      onConfirm: () => recreateIndex(indexType, customListCode)(dispatch),
    })
  );
};

const deleteData = async (dataType: ApiOrganisationDataType, organisationId: number, dispatch: Dispatch<any>) => {
  const descriptions = {
    [ApiOrganisationDataType.PERSON]: 'All persons and transactions will be deleted for this organisation.',
    [ApiOrganisationDataType.TRANSACTION]: 'All transactions will be deleted for this organisation.',
    [ApiOrganisationDataType.CONFIGURATION]:
      'All configuration will be reset or deleted into state as this organisation would have been newly created.',
  };

  dispatch(
    showConfirmationModal({
      title: 'Are you sure?',
      description: descriptions[dataType],
      confirmationText: 'Yes, delete it',
      cancelText: 'No, keep it',
      onConfirm: () => deleteOrganisationData(dataType, organisationId)(dispatch),
    })
  );
};

const calculatePersonScreenableValueIndex = async (dispatch: Dispatch<any>) => {
  dispatch(
    showConfirmationModal({
      title: 'Are you sure you wish to recalculate person screenable values?',
      confirmationText: 'Yes',
      cancelText: 'No',
      onConfirm: () => calculatePersonScreenableValues(dispatch),
    })
  );
};

export const System = () => {
  const dispatch = useDispatch();
  const { currentOrganisation } = useMe();

  const frontendVersion = `Frontend version: ${process.env.FE_APP_VERSION || 'unknown'}`;
  return (
    <SalvPage title='System' subTitle={frontendVersion}>
      <Has anyPermissions={[CAN_MODIFY_SEARCH_INDEX]}>
        <SalvCard className='m-b-16'>
          <SalvCard.Header>Dow Jones</SalvCard.Header>
          <SalvCard.Body>
            <SalvButton
              onClick={() => recreateSearchIndex(InternalApiIndexType.DOW_JONES, undefined, dispatch)}
              variant='danger-outline'
              size={Size.SMALL}
              label='Recreate all DJ indexes'
            />
          </SalvCard.Body>
        </SalvCard>
      </Has>
      <Has
        anyPermissions={[
          CAN_MODIFY_SEARCH_INDEX,
          CAN_DELETE_ORGANISATION_DATA,
          CAN_TRIGGER_PERSON_SCREENABLE_VALUE_CALCULATION,
        ]}
      >
        {currentOrganisation && <SystemWithOrganisation currentOrganisation={currentOrganisation} />}
      </Has>
    </SalvPage>
  );
};

export const SystemWithOrganisation = ({
  currentOrganisation,
}: {
  currentOrganisation: InternalApiCurrentUserOrganisation;
}) => {
  const dispatch = useDispatch();
  const customLists = useCustomLists();

  const organisationId = currentOrganisation.id;
  const features = currentOrganisation.features;

  const isMonitoringOrScreening =
    isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, features) ||
    isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, features);

  const filteredCustomLists = customLists.filter((list) => !list.code?.startsWith(ApiScreeningMatchProvider.DOW_JONES));

  return (
    <>
      <Has allPermissions={CAN_DELETE_ORGANISATION_DATA}>
        <SalvCard className='m-b-16'>
          <SalvCard.Header>Data</SalvCard.Header>
          <SalvCard.Body>
            <div className='d-flex flex-gap-8'>
              <SalvButton
                onClick={() => deleteData(ApiOrganisationDataType.CONFIGURATION, organisationId, dispatch)}
                variant='danger-outline'
                size={Size.SMALL}
                label='Reset organisation'
              />
              {isMonitoringOrScreening && (
                <SalvButton
                  onClick={() => deleteData(ApiOrganisationDataType.PERSON, organisationId!, dispatch)}
                  variant='danger-outline'
                  size={Size.SMALL}
                  label='Delete all persons'
                />
              )}
              {isMonitoringOrScreening && (
                <SalvButton
                  onClick={() => deleteData(ApiOrganisationDataType.TRANSACTION, organisationId!, dispatch)}
                  variant='danger-outline'
                  size={Size.SMALL}
                  label='Delete all transactions'
                />
              )}
            </div>
          </SalvCard.Body>
        </SalvCard>
      </Has>

      <Has allPermissions={CAN_MODIFY_SEARCH_INDEX}>
        {isMonitoringOrScreening && (
          <SalvCard className='m-b-16'>
            <SalvCard.Header>Indexes</SalvCard.Header>
            <SalvCard.Body>
              <div className='d-flex flex-gap-8'>
                <SalvButton
                  onClick={() => recreateSearchIndex(InternalApiIndexType.PERSON, undefined, dispatch)}
                  variant='danger-outline'
                  size={Size.SMALL}
                  label='Recreate PERSON index'
                />
                <SalvButton
                  onClick={() => recreateSearchIndex(InternalApiIndexType.TRANSACTION, undefined, dispatch)}
                  variant='danger-outline'
                  size={Size.SMALL}
                  label='Recreate TRANSACTION index'
                />
              </div>
            </SalvCard.Body>
          </SalvCard>
        )}
        {isMonitoringOrScreening && filteredCustomLists.length > 0 && (
          <SalvCard className='m-b-16'>
            <SalvCard.Header>Custom Lists</SalvCard.Header>
            <SalvCard.Body>
              <div className='d-flex flex-column flex-gap-12'>
                {filteredCustomLists.map((list) => (
                  <div key={list.id} className='d-flex justify-content-between align-items-center flex-gap-12'>
                    <span>{list.name}</span>
                    <div>
                      <SalvButton
                        onClick={() => recreateSearchIndex(undefined, list.code, dispatch)}
                        variant='danger-outline'
                        size={Size.SMALL}
                        label={`Recreate ${list.code} list index`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </SalvCard.Body>
          </SalvCard>
        )}
      </Has>

      <Has allPermissions={CAN_TRIGGER_PERSON_SCREENABLE_VALUE_CALCULATION}>
        {isMonitoringOrScreening && (
          <SalvCard>
            <SalvCard.Header>Person</SalvCard.Header>
            <SalvCard.Body className='p-a-16'>
              <SalvButton
                onClick={() => calculatePersonScreenableValueIndex(dispatch)}
                variant='danger-outline'
                size={Size.SMALL}
                label='Recreate PERSON screenable value index'
              />
            </SalvCard.Body>
          </SalvCard>
        )}
      </Has>
    </>
  );
};

export default System;
